import type { HeadingSegment } from '@/components/generals/animated-heading'

import Image from 'next/image'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Section } from '@/components/generals/section'
import { AnimatedHeading } from '@/components/generals/animated-heading'

type AboutSplitProps = {
    id: string
    segments: HeadingSegment[]
    items: { title: string; body: string }[]
    image: string
    alt: string
    /** Puts the image on the right, as the "personal note" block does. */
    imageRight?: boolean
}

export const AboutSplit = ({
    id,
    segments,
    items,
    image,
    alt,
    imageRight = false
}: AboutSplitProps) => {
    return (
        <Section
            id={id}
            className='py-[80px] lg:py-[100px]'
            containerClassName='items-stretch'
        >
            <div className='grid w-full grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16'>
                <Reveal
                    y={30}
                    blur={12}
                    className={cn(
                        'relative aspect-4/5 w-full overflow-hidden rounded-2xl bg-surface-1 ring-1 ring-white/8 sm:aspect-3/2 lg:aspect-4/5',
                        imageRight && 'lg:order-2'
                    )}
                >
                    <Image
                        src={image}
                        alt={alt}
                        fill
                        sizes='(max-width: 1024px) 100vw, 560px'
                        className='object-cover'
                    />
                </Reveal>

                <div className={cn('flex flex-col gap-8', imageRight && 'lg:order-1')}>
                    <AnimatedHeading
                        segments={segments}
                        className='text-[clamp(1.75rem,3.2vw,2.625rem)] leading-[1.15] font-medium tracking-[-0.04em] text-white'
                    />

                    <Reveal
                        y={24}
                        blur={10}
                        delay={0.1}
                        stagger={0.1}
                        className='flex flex-col gap-7'
                    >
                        {items.map((item) => (
                            <div
                                key={item.title}
                                className='flex flex-col gap-2.5'
                            >
                                <h3 className='text-lg leading-none font-medium tracking-[-0.02em] text-white'>
                                    {item.title}
                                </h3>

                                <p className='max-w-[56ch] text-[15px] leading-[1.6] tracking-[-0.01em] text-white/56'>
                                    {item.body}
                                </p>
                            </div>
                        ))}
                    </Reveal>
                </div>
            </div>
        </Section>
    )
}
